import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, UserPlus, UserMinus, TrendingDown, CalendarOff, UserX, Palmtree, TimerOff, ClipboardCheck, Receipt, FileX2, AlertTriangle,
} from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { reportAnchor, reportRange, shiftReportAnchor } from "../../lib/reportRange.js";
import { SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { ChartPanel } from "../../components/ui/charts/ChartPanel.js";
import { TrendArea } from "../../components/ui/charts/TrendArea.js";
import { BarChart } from "../../components/ui/charts/BarChart.js";
import { CategoryBars } from "../../components/ui/charts/CategoryBars.js";
import { ReportPage, FilterBar, ErrorBanner, OrgFilters, CardGrid, ReportStatCard, type OpenState } from "../../components/reports/ReportKit.js";
import { useUrlFilters, useOrgMeta, useReportData, toParams } from "../../components/reports/useReport.js";

interface TrendPoint { key: string; headcount: number; joiners: number; exits: number }
interface Dashboard {
  part: "all";
  month: string; from: string; to: string;
  cards: {
    headcount: { closing: number; opening: number };
    joiners: number; exits: number; attritionPct: number; annualisedPct: number;
    lopDays: number; leaveDays: number;
    probation: { onProbation: number; overdue: number; dueSoon: number; dueSoonDays: number };
    pendingLeave: { requests: number; days: number };
    pendingExpenses: { reports: number; amount: number };
    missingDocuments: { employees: number; of: number };
    exitedWithoutRecord: number;
  };
  trend: TrendPoint[];
  byDepartment: { id: string | null; name: string; count: number }[];
}
interface DashboardAttendance { part: "attendance"; month: string; absentDays: number; awaitingDays: number }

const DEFAULTS = { officeIds: "", departmentIds: "", anchor: "" };
const API = "/reports/hr/dashboard";

const MONTH_FMT = new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
const monthLabel = (key: string) => MONTH_FMT.format(new Date(`${key}-01T00:00:00Z`));
const num = (n: number) => n.toLocaleString("en-IN");
const money = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function HrDashboardPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const anchor = f.anchor || reportAnchor("month");
  const range = reportRange("month", anchor);
  const month = anchor.slice(0, 7);

  const params = useMemo(
    () => toParams({ officeIds: f.officeIds, departmentIds: f.departmentIds }, { month }),
    [f.officeIds, f.departmentIds, month],
  );
  // Absent days need the attendance grid over the whole scope: their own request, so the
  // rest of the page never waits on it.
  const attendanceParams = useMemo(() => {
    const p = new URLSearchParams(params);
    p.set("part", "attendance");
    return p;
  }, [params]);
  const { data, loading, error } = useReportData<Dashboard>(API, params, "HrDashboardPage");
  const att = useReportData<DashboardAttendance>(API, attendanceParams, "HrDashboardPage.attendance");

  const c = data?.cards;
  // The linked reports take the same month and org filters in their URL.
  const link = (path: string, extra: Record<string, string> = {}) => {
    const p = toParams({ officeIds: f.officeIds, departmentIds: f.departmentIds }, extra);
    const qs = p.toString();
    return qs ? `${path}?${qs}` : path;
  };
  const monthly = { period: "month", anchor };

  const trend = data?.trend ?? [];
  const barRows = (pick: (t: TrendPoint) => number) => trend.map((t) => ({ label: monthLabel(t.key), value: pick(t) }));

  return (
    <ReportPage
      title="HR Dashboard"
      description="The month at a glance. Every tile opens its report for the same month and filters."
    >
      <FilterBar>
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor("month", anchor, -1) })} aria-label="Previous month" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor("month", anchor, 1) })} aria-label="Next month" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
      </FilterBar>

      <ErrorBanner message={error} />

      <CardGrid>
        <Link to={link("/reports/headcount", monthly)} className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={Users} tone="brand" label="Headcount" className="flex-1"
            value={c ? num(c.headcount.closing) : "—"}
            caption={c ? `Opening ${num(c.headcount.opening)} · ${c.headcount.closing - c.headcount.opening >= 0 ? "+" : ""}${c.headcount.closing - c.headcount.opening}` : "End of month"}
          />
        </Link>
        <Link to={link("/reports/headcount", monthly)} className="flex min-w-0 no-underline">
          <ReportStatCard icon={UserPlus} tone="success" label="Joiners" className="flex-1" value={c?.joiners ?? "—"} caption={range.label} />
        </Link>
        <Link to={link("/reports/attrition", monthly)} className="flex min-w-0 no-underline">
          <ReportStatCard icon={UserMinus} tone="danger" label="Exits" className="flex-1" value={c?.exits ?? "—"} caption="Last working day in the month" />
        </Link>
        <Link to={link("/reports/attrition", monthly)} className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={TrendingDown} tone="rose" label="Attrition" className="flex-1"
            value={c ? <>{c.attritionPct}<SummaryStatSuffix> %</SummaryStatSuffix></> : "—"}
            caption={c ? `${c.annualisedPct}% annualised` : undefined}
          />
        </Link>
        <Link to={link("/reports/lop", monthly)} className="flex min-w-0 no-underline">
          <ReportStatCard icon={CalendarOff} tone="warning" label="LOP Days" className="flex-1" value={c?.lopDays ?? "—"} caption="Approved, unpaid" />
        </Link>
        <Link to={link("/reports/lop", monthly)} className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={UserX} tone="danger" label="Unauthorised Absent" className="flex-1"
            value={att.data ? att.data.absentDays : att.error ? "n/a" : "…"}
            caption={att.data?.awaitingDays ? `${att.data.awaitingDays} days awaiting upload not counted` : att.error ? "Could not load" : "Days, no leave or punch"}
          />
        </Link>
        <Link to={link("/reports/leave", { year: month.slice(0, 4) })} className="flex min-w-0 no-underline">
          <ReportStatCard icon={Palmtree} tone="info" label="Leave Days Taken" className="flex-1" value={c?.leaveDays ?? "—"} caption="Approved, excl. work from home" />
        </Link>
        <Link to={link("/reports/probation", { ...monthly, states: "overdue,dueSoon" })} className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={TimerOff} tone="accent" label="Probation Overdue" className="flex-1"
            value={c?.probation.overdue ?? "—"}
            caption={c ? `${c.probation.dueSoon} due in ${c.probation.dueSoonDays} days · as of today` : undefined}
          />
        </Link>
        <Link to="/leave" className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={ClipboardCheck} tone="warning" label="Pending Leave Approvals" className="flex-1"
            value={c?.pendingLeave.requests ?? "—"} caption={c ? `${c.pendingLeave.days} days waiting` : undefined}
          />
        </Link>
        <Link to="/expenses" className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={Receipt} tone="cyan" label="Pending Expense Approvals" className="flex-1"
            value={c?.pendingExpenses.reports ?? "—"} caption={c ? `${money(c.pendingExpenses.amount)} waiting` : undefined}
          />
        </Link>
        <Link to={link("/reports/documents", { completion: "incomplete" })} className="flex min-w-0 no-underline">
          <ReportStatCard
            icon={FileX2} tone="rose" label="Missing Documents" className="flex-1"
            value={c?.missingDocuments.employees ?? "—"} caption={c ? `of ${c.missingDocuments.of} current employees` : undefined}
          />
        </Link>
      </CardGrid>

      {c && c.exitedWithoutRecord > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[13px] text-[var(--color-warning-fg)]">
          <AlertTriangle size={14} />
          {c.exitedWithoutRecord} terminated employee(s) have no offboarding record, so they have no exit date and are left out of the headcount figures.
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <ChartPanel title="Headcount" hint="Closing headcount, last 12 months">
          <TrendArea
            data={trend.map((t) => ({ label: monthLabel(t.key), value: t.headcount }))}
            formatValue={num} height={220} emptyMessage={loading ? "Loading…" : "No data"}
          />
        </ChartPanel>
        <ChartPanel title="Headcount by department" hint={`End of ${range.label}, by current department`}>
          <CategoryBars
            data={(data?.byDepartment ?? []).map((d) => ({ id: d.id, name: d.name, value: d.count }))}
            maxBars={10} labelWidth={120} formatValue={num} emptyMessage={loading ? "Loading…" : "No employees"}
          />
        </ChartPanel>
        <ChartPanel title="Joiners" hint="By hire date, last 12 months">
          <BarChart data={barRows((t) => t.joiners)} formatValue={num} defaultColorClass="fill-[var(--color-success-fg)]" emptyText={loading ? "Loading…" : "No data"} />
        </ChartPanel>
        <ChartPanel title="Exits" hint="By last working day, last 12 months">
          <BarChart data={barRows((t) => t.exits)} formatValue={num} defaultColorClass="fill-[var(--color-error-fg)]" emptyText={loading ? "Loading…" : "No data"} />
        </ChartPanel>
      </div>
    </ReportPage>
  );
}
